// PropertyDeepDive — main entry point
import { apiGet, apiPost } from './api.js';
import { initSearch } from './search.js';
import { initMap, flyTo, setMarker } from './map.js';
import { initPropertyPage } from './property.js';
import { formatBBL, formatSqft, boroughName } from './utils.js';

// ─── Toast notification system ───
window.showToast = function (message, type = 'info', durationMs = 4000) {
  const container = document.getElementById('toast-container');
  if (!container) return;
  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.textContent = message;
  container.appendChild(toast);
  setTimeout(() => {
    toast.classList.add('toast-exit');
    toast.addEventListener('animationend', () => toast.remove());
  }, durationMs);
};

// ─── Detect page ───
const isPropertyPage = window.location.pathname.includes('property');

// ─── Auth state ───
let currentUser = null;

async function checkAuth() {
  try {
    const data = await apiGet('/api/auth/me');
    currentUser = data.user;
    updateAuthUI();
  } catch (e) {
    currentUser = null;
    updateAuthUI();
  }
}

function updateAuthUI() {
  const authButtons = document.getElementById('auth-buttons');
  const userArea = document.getElementById('user-area');
  if (!authButtons || !userArea) return;

  if (currentUser) {
    authButtons.style.display = 'none';
    userArea.style.display = 'block';
    document.getElementById('user-name').textContent = currentUser.name;
    document.getElementById('tier-display').textContent = `${currentUser.tier.charAt(0).toUpperCase() + currentUser.tier.slice(1)} tier`;
    const limit = { free: 5, pro: 100, enterprise: '\u221E' }[currentUser.tier] || 5;
    document.getElementById('lookups-display').textContent = `${currentUser.daily_lookups || 0}/${limit} lookups today`;
  } else {
    authButtons.style.display = 'flex';
    userArea.style.display = 'none';
  }
}

// ─── Auth modal ───
let authMode = 'login';

window.showModal = function (mode) {
  authMode = mode;
  const modal = document.getElementById('auth-modal');
  const title = document.getElementById('modal-title');
  const regFields = document.getElementById('register-fields');
  const submit = document.getElementById('auth-submit');
  const switchEl = document.getElementById('auth-switch');

  modal.classList.add('active');
  document.getElementById('auth-error').style.display = 'none';
  // Switch password field to type=password only when modal opens (prevents Chrome autofill on search)
  document.getElementById('auth-password').type = 'password';
  document.getElementById('auth-email').type = 'email';

  if (mode === 'register') {
    title.textContent = 'Create account';
    regFields.style.display = 'block';
    submit.textContent = 'Sign up';
    switchEl.innerHTML = 'Already have an account? <a href="/?login=1" onclick="switchModal(); return false;">Log in</a>';
  } else {
    title.textContent = 'Log in';
    regFields.style.display = 'none';
    submit.textContent = 'Log in';
    switchEl.innerHTML = 'Don\'t have an account? <a href="/?signup=1" onclick="switchModal(); return false;">Sign up</a>';
  }

  // Focus first input
  setTimeout(() => {
    const firstInput = mode === 'register'
      ? document.getElementById('reg-name')
      : document.getElementById('auth-email');
    firstInput?.focus();
  }, 100);
};

window.switchModal = function () {
  window.showModal(authMode === 'login' ? 'register' : 'login');
};

function openRequestedAuthModal() {
  if (isPropertyPage || currentUser) return;

  const params = new URLSearchParams(window.location.search);
  const wantsSignup = params.get('signup') === '1' || params.get('register') === '1' || params.get('upgrade') === '1';
  const wantsLogin = params.get('login') === '1';

  if (wantsSignup || wantsLogin) {
    window.showModal(wantsSignup ? 'register' : 'login');
  }
}

window.submitAuth = async function () {
  const email = document.getElementById('auth-email').value.trim();
  const password = document.getElementById('auth-password').value;
  const errEl = document.getElementById('auth-error');
  const submitBtn = document.getElementById('auth-submit');

  // Client-side validation
  if (!email) {
    errEl.textContent = 'Email is required';
    errEl.style.display = 'block';
    return;
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errEl.textContent = 'Please enter a valid email address';
    errEl.style.display = 'block';
    return;
  }
  if (password.length < 8) {
    errEl.textContent = 'Password must be at least 8 characters';
    errEl.style.display = 'block';
    return;
  }

  // Disable button during request
  submitBtn.disabled = true;
  submitBtn.textContent = authMode === 'register' ? 'Creating account...' : 'Logging in...';

  try {
    if (authMode === 'register') {
      const name = document.getElementById('reg-name').value.trim();
      if (!name) {
        errEl.textContent = 'Name is required';
        errEl.style.display = 'block';
        return;
      }
      await apiPost('/api/auth/register', { email, name, password });
      window.showToast('Account created successfully!', 'success');
    } else {
      await apiPost('/api/auth/login', { email, password });
      window.showToast('Welcome back!', 'success');
    }
    document.getElementById('auth-modal').classList.remove('active');
    // Reset to text type to prevent Chrome autofill on search input
    document.getElementById('auth-password').type = 'text';
    document.getElementById('auth-email').type = 'text';
    await checkAuth();
  } catch (e) {
    errEl.textContent = e.error || 'Something went wrong';
    errEl.style.display = 'block';
  } finally {
    submitBtn.disabled = false;
    submitBtn.textContent = authMode === 'register' ? 'Sign up' : 'Log in';
  }
};

window.toggleUserMenu = function () {
  document.getElementById('user-dropdown').classList.toggle('active');
};

window.logout = async function () {
  try {
    await apiPost('/api/auth/me', {});
  } catch (e) { /* ignore */ }
  currentUser = null;
  updateAuthUI();
  window.showToast('Logged out', 'info');
};

// Enter key to submit auth form
document.getElementById('auth-password')?.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') window.submitAuth();
});

// Close modal on overlay click
document.getElementById('auth-modal')?.addEventListener('click', function (e) {
  if (e.target === this) {
    this.classList.remove('active');
    // Reset fields to text type to prevent Chrome autofill on search input
    document.getElementById('auth-password').type = 'text';
    document.getElementById('auth-email').type = 'text';
  }
});

// Close user dropdown on outside click
document.addEventListener('click', function (e) {
  const userArea = document.getElementById('user-area');
  const dropdown = document.getElementById('user-dropdown');
  if (userArea && dropdown && !userArea.contains(e.target)) {
    dropdown.classList.remove('active');
  }
});

// ─── Mobile map toggle ───
let showingMap = false;
window.toggleMobileMap = function () {
  const main = document.getElementById('main');
  const btn = document.getElementById('mobile-map-toggle');
  if (!main || !btn) return;
  showingMap = !showingMap;
  main.classList.toggle('show-map', showingMap);
  btn.textContent = showingMap ? 'Show Search' : 'Show Map';
};

// ─── Initialize ───

if (isPropertyPage) {
  // Property detail page
  checkAuth();
  initPropertyPage();
} else {
  // Home / search page
  checkAuth().finally(openRequestedAuthModal);

  const map = initMap('map');

  initSearch((result) => {
    // Track search selection
    if (typeof window.trackEvent === 'function') window.trackEvent('search', 'query', result.label || '');

    // When a search result is selected
    if (result.lat && result.lng) {
      flyTo(result.lng, result.lat);
      setMarker(result.lng, result.lat,
        `<strong>${result.label}</strong>${result.bbl ? `<br>BBL: ${result.bbl}` : ''}`
      );
    }

    // Show property card in sidebar
    if (result.bbl || result.type === 'nys' || result.type === 'nj' || result.type === 'nc' || result.type === 'ca') {
      showPropertyCard(result);
    }
  });
}

async function showPropertyCard(result) {
  const panel = document.getElementById('property-results');
  const list = document.getElementById('results-list');
  const empty = document.getElementById('empty-state');

  if (!panel || !list) return;
  empty.style.display = 'none';
  panel.style.display = 'block';

  // Build property URL and metadata based on region
  const bbl = result.bbl;
  const isNYS = result.type === 'nys';
  const isNJ = result.type === 'nj';
  const isNC = result.type === 'nc';
  const isCA = result.type === 'ca';
  const isPHL = result.type === 'phl';
  const isIL = result.type === 'il';
  const isCT = result.type === 'ct';
  let propertyUrl, basicMeta;
  const basicAddress = result.label || 'Unknown address';

  if (result.type === 'nj') {
    propertyUrl = `/property?region=nj&pamsPin=${encodeURIComponent(result.pamsPin)}&address=${encodeURIComponent(result.address)}&county=${encodeURIComponent(result.county || '')}&municipality=${encodeURIComponent(result.municipality || '')}`;
    basicMeta = `${result.municipality || ''}, ${result.county || ''} County \u00B7 New Jersey`;
  } else if (result.type === 'nc') {
    propertyUrl = `/property?region=nc&parcelId=${encodeURIComponent(result.parcelId)}&address=${encodeURIComponent(result.address)}&county=${encodeURIComponent(result.county || '')}`;
    basicMeta = `${result.city || ''}, ${result.county || ''} County \u00B7 North Carolina`;
  } else if (result.type === 'ca') {
    propertyUrl = `/property?region=ca&address=${encodeURIComponent(result.address)}&city=${encodeURIComponent(result.city || '')}&zipcode=${encodeURIComponent(result.zipcode || '')}&lat=${result.lat || ''}&lng=${result.lng || ''}`;
    basicMeta = `${result.city || ''}${result.zipcode ? ` ${result.zipcode}` : ''} \u00B7 California`;
  } else if (result.type === 'phl') {
    propertyUrl = `/property?region=phl&opa=${encodeURIComponent(result.opa || '')}&address=${encodeURIComponent(result.address || '')}`;
    basicMeta = `Philadelphia, PA \u00B7 OPA ${result.opa || ''}`;
  } else if (result.type === 'il') {
    propertyUrl = `/property?region=il&pin=${encodeURIComponent(result.pin || '')}&address=${encodeURIComponent(result.address || '')}`;
    basicMeta = `${result.city || ''}, ${result.county || ''} County \u00B7 Illinois`;
  } else if (result.type === 'ct') {
    propertyUrl = `/property?region=ct&link=${encodeURIComponent(result.link || '')}&address=${encodeURIComponent(result.address || '')}&city=${encodeURIComponent(result.city || '')}`;
    basicMeta = `${result.city || ''}${result.zipcode ? ` ${result.zipcode}` : ''} \u00B7 Connecticut`;
  } else if (result.type === 'nys') {
    propertyUrl = `/property?region=nys&swis=${result.swis}&parcel=${encodeURIComponent(result.parcel)}`;
    basicMeta = `${result.county || ''} County \u00B7 ${result.town || 'NY State'}`;
  } else {
    propertyUrl = `/property?bbl=${bbl}`;
    basicMeta = [result.borough ? boroughName(result.borough) || result.borough : '', bbl ? `BBL ${formatBBL(bbl)}` : ''].filter(Boolean).join(' \u00B7 ');
  }

  // Show skeleton loading state immediately
  list.innerHTML = `
    <a class="property-card" href="${propertyUrl}" style="display:block;text-decoration:none;color:inherit;cursor:pointer;">
      <div class="address">${basicAddress}</div>
      <div class="meta">${basicMeta}</div>
      <div class="tags"><span class="tag" style="background:#f1f5f9;color:transparent;width:60px;">Loading</span><span class="tag" style="background:#f1f5f9;color:transparent;width:80px;">Loading</span></div>
    </a>
  `;

  if (isNYS || isNJ || isNC || isCA || isPHL || isIL || isCT) {
    // Out-of-NYC: show card immediately with search result data (no PLUTO enrichment)
    const stateTag = isNYS ? 'NY State' : isNJ ? 'New Jersey' : isNC ? 'North Carolina' : isCA ? 'California' : isPHL ? 'Pennsylvania' : isIL ? 'Illinois' : 'Connecticut';
    list.innerHTML = `
      <a class="property-card" href="${propertyUrl}" style="display:block;text-decoration:none;color:inherit;cursor:pointer;">
        <div class="address">${result.address || basicAddress}</div>
        <div class="meta">${basicMeta}</div>
        <div class="tags">
          ${result.propertyClass ? `<span class="tag">${result.propertyClass}</span>` : ''}
          <span class="tag">${stateTag}</span>
        </div>
      </a>
    `;
    return;
  }

  // NYC: Try to enrich with PLUTO data
  try {
    const data = await apiGet(`/api/property?bbl=${bbl}`);
    const p = data.property;

    list.innerHTML = `
      <a class="property-card" href="/property?bbl=${String(p.bbl).replace(/\..*$/,'')}" style="display:block;text-decoration:none;color:inherit;cursor:pointer;">
        <div class="address">${p.address || basicAddress}</div>
        <div class="meta">${p.boroughName || boroughName(p.borough) || ''} \u00B7 BBL ${formatBBL(p.bbl)}</div>
        <div class="tags">
          ${p.bldg_class ? `<span class="tag">Class ${p.bldg_class}</span>` : ''}
          ${p.year_built ? `<span class="tag">Built ${p.year_built}</span>` : ''}
          ${p.bldg_area ? `<span class="tag">${formatSqft(p.bldg_area)}</span>` : ''}
          ${p.zoning_dist1 ? `<span class="tag">${p.zoning_dist1}</span>` : ''}
          ${p.units_total ? `<span class="tag">${p.units_total} units</span>` : ''}
        </div>
      </a>
    `;
  } catch (e) {
    list.innerHTML = `
      <a class="property-card" href="${propertyUrl}" style="display:block;text-decoration:none;color:inherit;cursor:pointer;">
        <div class="address">${basicAddress}</div>
        <div class="meta">${basicMeta}</div>
      </a>
    `;
  }
}
